/**
 * Flattens a Route into one continuous polyline plus a step lookup table, so
 * that a GPS fix can be turned into "which step am I on and how far to the
 * next turn" with a couple of array lookups.
 *
 * We rebuild the line from the STEP geometries rather than using
 * `route.geometry`. They describe the same path, but only the step version
 * gives us exact vertex indices for step boundaries - and being off by even one
 * vertex at a junction makes the maneuver fire late.
 */

import type { LngLat, Route, RouteStep } from '../core/types';
import {
  bearing,
  bearingDelta,
  cumulativeDistances,
  distance,
  nearestPointOnSegment,
} from '../core/geo';
import type { SnapResult } from '../core/types';
import { significanceMap } from './significance';
import { buildDivergences, type Divergence } from './divergence';

export interface IndexedStep {
  legIndex: number;
  stepIndex: number;
  step: RouteStep;
  /** Vertex index in the combined line where this step starts / ends. */
  startVertex: number;
  endVertex: number;
  /** Distance along the whole route to the start / end of this step, metres. */
  startDistance: number;
  endDistance: number;
}

export interface SnapContext {
  /** Direction of travel from GPS, degrees clockwise from north. */
  heading?: number | null;
  /** Horizontal GPS uncertainty, metres. */
  accuracy?: number;
  /** True only while the vehicle is moving fast enough for heading to be useful. */
  moving?: boolean;
  /** Expected route distance at this fix, derived from the previous accepted fix. */
  expectedAlong?: number;
  /** Furthest plausible forward progress for a recent fix; omitted after a long pause. */
  maxForwardAlong?: number;
}

interface SnapCandidate {
  dist: number;
  index: number;
  t: number;
  point: LngLat;
  along: number;
  course: number;
}

// Wide enough for a noisy moving heading, but narrower than the diagonal
// connector that a stale reversed heading can otherwise mistake for "done".
const ALIGNED_COURSE_DEG = 45;

export class RouteIndex {
  readonly coords: LngLat[] = [];
  readonly cumulative: number[];
  readonly steps: IndexedStep[] = [];
  readonly totalDistance: number;
  /**
   * `significant[i]` describes the maneuver at the START of step i: true when
   * it needs a decision from the driver, false for the lane-keeping nudges
   * Mapbox emits along a single numbered road. See significance.ts.
   */
  readonly significant: boolean[] = [];
  /**
   * Lane splits along the route that Mapbox does not call a maneuver - the
   * junctions nothing else in the app looks at. See nav/divergence.ts.
   */
  readonly divergences: Divergence[];

  constructor(readonly route: Route) {
    for (let legIndex = 0; legIndex < route.legs.length; legIndex++) {
      const leg = route.legs[legIndex];
      for (let stepIndex = 0; stepIndex < leg.steps.length; stepIndex++) {
        const step = leg.steps[stepIndex];
        const line = step.geometry?.coordinates ?? [];
        const startVertex = Math.max(0, this.coords.length - (this.coords.length ? 1 : 0));

        // Steps share a vertex with their predecessor; drop the duplicate.
        const slice = this.coords.length > 0 ? line.slice(1) : line;
        for (const c of slice) this.coords.push(c);

        this.steps.push({
          legIndex,
          stepIndex,
          step,
          startVertex,
          endVertex: Math.max(startVertex, this.coords.length - 1),
          startDistance: 0,
          endDistance: 0,
        });
      }
    }

    if (this.coords.length === 0) this.coords.push([0, 0]);
    this.cumulative = cumulativeDistances(this.coords);
    this.totalDistance = this.cumulative[this.cumulative.length - 1];

    for (const s of this.steps) {
      s.startDistance = this.cumulative[s.startVertex] ?? 0;
      s.endDistance = this.cumulative[s.endVertex] ?? this.totalDistance;
    }

    const sig = significanceMap(this.steps.map((s) => s.step));
    for (let i = 0; i < this.steps.length; i++) {
      this.significant[i] = i === 0 ? false : sig[i];
    }
    // The final step is the arrival; it must never be merged away.
    if (this.steps.length > 1) this.significant[this.steps.length - 1] = true;

    // Splits that are not maneuvers. Computed once here because it walks every
    // intersection on the route - far too much to redo on each GPS fix.
    this.divergences = buildDivergences(this.steps, this.cumulative, this.significant);
  }

  /**
   * Index of the next step that BEGINS with a maneuver worth announcing,
   * searching strictly after `fromStep`. Falls back to the last step (arrival).
   */
  nextSignificant(fromStep: number): number {
    for (let i = Math.max(0, fromStep) + 1; i < this.steps.length; i++) {
      if (this.significant[i]) return i;
    }
    return this.steps.length - 1;
  }

  /** Index of the previous significant maneuver, or -1 (route start). */
  previousSignificant(beforeStep: number): number {
    for (let i = Math.min(beforeStep, this.steps.length) - 1; i > 0; i--) {
      if (this.significant[i]) return i;
    }
    return -1;
  }

  /**
   * Snap a position onto the route.
   *
   * `hintVertex` limits the search to a window around the last known position,
   * which is both faster and — more importantly — correct on routes that cross
   * themselves or run back alongside an earlier section. A global nearest-point
   * search would happily teleport you onto the opposite carriageway.
   */
  snap(
    p: LngLat,
    hintVertex: number | null = null,
    windowVertices = 400,
    context: SnapContext = {},
  ): SnapResult {
    const n = this.coords.length;
    let from = 0;
    let to = n - 1;
    if (hintVertex !== null) {
      from = Math.max(0, hintVertex - 40);
      to = Math.min(n - 1, hintVertex + windowVertices);
    }

    const candidates: SnapCandidate[] = [];
    const project = (i: number): SnapCandidate => {
      const a = this.coords[i];
      const b = this.coords[i + 1] ?? a;
      const r = nearestPointOnSegment(p, a, b);
      const segLength = distance(a, b);
      return {
        dist: r.distance,
        index: i,
        t: r.t,
        point: r.point,
        along: (this.cumulative[i] ?? 0) + segLength * r.t,
        course: segLength > 0 ? bearing(a, b) : 0,
      };
    };
    for (let i = from; i < to; i++) candidates.push(project(i));

    if (!candidates.length) candidates.push(project(0));
    let best = candidates.reduce(
      (winner, candidate) => (candidate.dist < winner.dist ? candidate : winner),
      candidates[0],
    );

    // The windowed search failed to find anything plausible - the driver has
    // probably jumped (tunnel exit, app resumed). Fall back to a full scan.
    if (best.dist > 120 && (from > 0 || to < n - 1)) {
      candidates.length = 0;
      for (let i = 0; i < n - 1; i++) {
        candidates.push(project(i));
      }
      if (!candidates.length) candidates.push(project(0));
      best = candidates.reduce((winner, candidate) =>
        candidate.dist < winner.dist ? candidate : winner,
      );
    }

    /*
     * A U-turn puts two opposite-direction route sections within one GPS
     * accuracy circle. Pure nearest-point matching can then jump across the
     * median and permanently consume the U-turn. Only arbitrate candidates
     * that are genuinely ambiguous; a clearly closer line still wins.
     */
    const accuracy = Number.isFinite(context.accuracy) ? Math.max(0, context.accuracy ?? 0) : 0;
    const ambiguityM = Math.max(15, Math.min(35, accuracy));
    let plausible = candidates.filter((candidate) => candidate.dist <= best.dist + ambiguityM);

    // A single stale heading must not skip a kilometre of U-turn connector.
    // For recent fixes, first remove candidates that exceed physically
    // plausible progress. If every ambiguous candidate exceeds the bound, the
    // position itself has jumped too far, so continuity decides and heading is
    // deliberately denied the tie-break. Long-pause reacquisition omits the
    // bound and can still use a confirmed moving heading.
    let headingMayOverrideContinuity = true;
    if (context.maxForwardAlong != null && Number.isFinite(context.maxForwardAlong)) {
      const feasible = plausible.filter((candidate) => candidate.along <= context.maxForwardAlong!);
      if (feasible.length) plausible = feasible;
      else headingMayOverrideContinuity = false;
    }

    const heading = context.heading;
    if (
      headingMayOverrideContinuity &&
      context.moving &&
      accuracy <= 60 &&
      heading != null &&
      Number.isFinite(heading)
    ) {
      const aligned = plausible.filter(
        (candidate) => Math.abs(bearingDelta(heading, candidate.course)) <= ALIGNED_COURSE_DEG,
      );
      // A moving heading is the positive evidence that a real U-turn has
      // completed. Filter before applying continuity so a long pause can
      // advance onto the reversed carriageway without allowing a null-heading
      // lateral drift to consume the maneuver.
      if (aligned.length) plausible = aligned;
    }

    if (context.expectedAlong != null && Number.isFinite(context.expectedAlong)) {
      best = plausible.reduce((winner, candidate) => {
        const winnerDelta = Math.abs(winner.along - context.expectedAlong!);
        const candidateDelta = Math.abs(candidate.along - context.expectedAlong!);
        if (candidateDelta !== winnerDelta) return candidateDelta < winnerDelta ? candidate : winner;
        return candidate.dist < winner.dist ? candidate : winner;
      });
    } else {
      best = plausible.reduce((winner, candidate) =>
        candidate.dist < winner.dist ? candidate : winner,
      );
    }

    return {
      location: best.point,
      deviation: best.dist,
      segmentIndex: best.index,
      distanceAlongRoute: best.along,
      courseOnRoute: best.course,
    };
  }

  /** The step containing a given distance along the route. */
  stepAt(distanceAlong: number, fromIndex = 0): number {
    for (let i = Math.max(0, fromIndex); i < this.steps.length; i++) {
      if (distanceAlong < this.steps[i].endDistance) return i;
    }
    return this.steps.length - 1;
  }
}
